import { supabase } from './supabase';
import { calculateFromTTC } from '@/utils/taxCalculator';
import { computeOrderAdviceKeys, isOrderSkippable } from '@/utils/orderAdviceRules';

// Types pour les bons de commande (adaptés à la structure existante)
export interface Order {
  id: string;
  order_number?: string;
  client_id?: string;
  vendor_id?: string;
  order_type: string;
  order_date: string;
  delivery_date?: string;
  delivery_info?: any; // JSON avec les informations de livraison
  total_htva: number;
  total_tva: number;
  total_ttc: number;
  status: string;
  created_at: string;
  updated_at?: string;
}

export interface OrderItem {
  id: string;
  order_id?: string;
  article_id?: string;
  quantity: number;
  unit_price: number;
  discount?: number;
  total_price: number;
  tax_rate?: number;
}

export interface OrderWithClient {
  id: string;
  order_number?: string;
  legacy_order_number?: string;
  order_date: string;
  delivery_date?: string;
  status: string;
  subtotal_ht: number;
  total_tax: number;
  total_ttc: number;
  created_at: string;
  order_type: string;
  client_name?: string;
  client_email?: string;
  client_phone?: string;
  client_postal_code?: string;
  client_city?: string;
  items_count: number;
  paid_amount: number;
  remaining_amount: number;
  store_id?: string;
  store_name?: string;
  store_initiales?: string;
  is_invoiced?: boolean;
  is_closed?: boolean;
}

export interface OrderItemDetail {
  id: string;
  order_id: string;
  order_number?: string;
  quantity: number;
  unit_price: number;
  discount?: number;
  tax_rate?: number;
  total_price: number;
  tax_amount?: number;
  total_with_tax?: number;
  article_code?: string;
  article_reference?: string;
  article_name?: string;
  article_description?: string;
}

export interface CreateOrderData {
  client_id?: string;
  vendor_id?: string;
  store_id?: string;
  order_type: string;
  order_date?: string;
  delivery_date?: string;
  status?: string;
}

export interface UpdateOrderData {
  client_id?: string;
  vendor_id?: string;
  order_type?: string;
  order_date?: string;
  delivery_date?: string | null; // Peut être null pour vider le champ
  delivery_info?: any; // JSON avec les informations de livraison
  status?: string;
  total_tva?: number;
  total_ttc?: number;
}

export interface CreateOrderItemData {
  order_id: string;
  article_id?: string; // Temporairement nullable jusqu'à correction du schéma DB
  quantity: number;
  unit_price: number;
  tax_rate?: number;
  // Nouvelles colonnes pour stocker les infos de l'article
  article_name?: string;
  article_code?: string;
  article_reference?: string;
  article_description?: string;
  // Colonnes de totaux selon le schéma DB
  line_total_ht?: number;
  line_total_tax?: number;
  line_total_ttc?: number;
  // Colonne total_price pour compatibilité
  total_price?: number;
  discount?: number;
}

export interface UpdateOrderItemData {
  quantity?: number;
  unit_price?: number;
  discount?: number;
  tax_rate?: number;
  total_price?: number;
  // Informations de l'article pour affichage
  article_name?: string;
  article_code?: string;
  article_reference?: string;
  article_description?: string;
}

export interface OrdersPaginatedResponse {
  orders: OrderWithClient[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
}

class OrderService2 {
  /**
   * Récupérer une commande par son ID
   */
  async getOrderById(id: string): Promise<Order | null> {
    try {
      const { data, error } = await supabase
        .from('orders2')
        .select(
          'id,order_number,client_id,vendor_id,order_type,order_date,delivery_date,delivery_info,total_htva,total_tva,total_ttc,status,created_at'
        )
        .eq('id', id)
        .single();

      if (error) {
        console.error('Erreur lors de la récupération de la commande:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Erreur dans getOrderById2:', error);
      throw error;
    }
  }

  /**
   * Récupère les IDs des commandes précédente et suivante (ordre created_at desc).
   * previousId = commande plus récente, nextId = commande plus ancienne.
   */
  async getOrderNeighbourIds(
    currentOrderId: string,
    storeId?: string
  ): Promise<{ previousId: string | null; nextId: string | null }> {
    let query = supabase
      .from('orders2')
      .select('id, is_closed, total_ttc, delivery_info')
      .order('created_at', { ascending: false })
      .limit(2000);

    if (storeId) {
      query = query.eq('store_id', storeId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Erreur getOrderNeighbourIds:', error);
      return { previousId: null, nextId: null };
    }

    const rows = data || [];
    const ids = rows.map((row: { id: string }) => row.id);
    const index = ids.indexOf(currentOrderId);
    if (index === -1) return { previousId: null, nextId: null };

    // "Suivant" = previousId (tri desc par created_at) → on saute les documents déjà
    // clôturés, ainsi que ceux dont "Conseils de gestion" ne demande aucune action manuelle
    // (Ne pas traiter / Document vide / Pas encore livré). "Précédent" (nextId) reste
    // inchangé : simple voisin adjacent, sans filtre.
    const candidateIds = rows.slice(0, index).map(r => r.id);
    const paymentsByOrder = new Map<string, { amount: number; payment_method: string | null }[]>();
    const CHUNK_SIZE = 100;
    for (let i = 0; i < candidateIds.length; i += CHUNK_SIZE) {
      const chunk = candidateIds.slice(i, i + CHUNK_SIZE);
      const { data: paymentsChunk, error: paymentsError } = await supabase
        .from('payments2')
        .select('order_id, amount, payment_method')
        .in('order_id', chunk);
      if (paymentsError) {
        console.error('Erreur récupération paiements pour getOrderNeighbourIds:', paymentsError);
        break;
      }
      (paymentsChunk || []).forEach(p => {
        const list = paymentsByOrder.get(p.order_id) || [];
        list.push({ amount: p.amount || 0, payment_method: p.payment_method });
        paymentsByOrder.set(p.order_id, list);
      });
    }

    let previousId: string | null = null;
    for (let i = index - 1; i >= 0; i--) {
      const row = rows[i];
      if (row.is_closed) continue;

      const payments = paymentsByOrder.get(row.id) || [];
      const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
      const solde = (row.total_ttc || 0) - totalPaid;
      const deliveryInfo: any = row.delivery_info || {};

      const keys = computeOrderAdviceKeys({
        solde,
        payments,
        deliveryDate: deliveryInfo.date,
        deliveryTiming: deliveryInfo.timing,
        isOrderClosed: false,
      });

      if (isOrderSkippable(keys)) continue;

      previousId = row.id;
      break;
    }

    return {
      previousId,
      nextId: index < ids.length - 1 ? ids[index + 1]! : null,
    };
  }

  /**
   * Récupérer les lignes d'une commande depuis order_items2
   */
  async getOrderItems(orderId: string): Promise<OrderItemDetail[]> {
    try {
      const { data, error } = await supabase
        .from('order_items2')
        .select(
          'id,order_id,quantity,unit_price,discount,tax_rate,total_price,article_code,article_reference,article_name,article_description,created_at'
        )
        .eq('order_id', orderId)
        .order('created_at', { ascending: true }); // Trier par created_at ASC (premier article ajouté en haut)

      if (error) {
        console.error('❌ Erreur lors de la récupération des lignes de commande:', error);
        throw error;
      }

      // Transformer les données pour correspondre à OrderItemDetail
      const transformedData: OrderItemDetail[] = (data || []).map((item: any) => ({
        id: item.id,
        order_id: item.order_id,
        order_number: orderId,
        quantity: item.quantity,
        unit_price: item.unit_price,
        discount: item.discount || 0,
        tax_rate: item.tax_rate || 21,
        total_price: item.total_price || item.line_total_ttc || item.quantity * item.unit_price,
        tax_amount:
          item.line_total_tax ||
          calculateFromTTC(
            item.total_price || item.line_total_ttc || item.quantity * item.unit_price,
            item.tax_rate || 21
          ).tva,
        total_with_tax: item.line_total_ttc || item.total_price || item.quantity * item.unit_price,
        article_code: item.article_code || '',
        article_reference: item.article_reference || '',
        article_name: item.article_name || 'Article à définir',
        article_description: item.article_description || '',
      }));

      return transformedData;
    } catch (error) {
      console.error('Erreur dans getOrderItems2:', error);
      throw error;
    }
  }

  /**
   * Ajouter une ligne à une commande dans order_items2
   */
  async addOrderItem(itemData: CreateOrderItemData): Promise<OrderItem | null> {
    try {
      // Get next position for this order
      const { data: existingItems } = await supabase
        .from('order_items2')
        .select('position')
        .eq('order_id', itemData.order_id)
        .order('position', { ascending: false })
        .limit(1);

      const maxPosition = existingItems?.[0]?.position ?? 0;
      const nextPosition = maxPosition + 1;

      const { data, error } = await supabase
        .from('order_items2')
        .insert([{ ...itemData, position: nextPosition }])
        .select()
        .single();

      if (error) {
        console.error("❌ Erreur lors de l'ajout de la ligne de commande:", error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Erreur dans addOrderItem2:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour les positions des articles (drag & drop)
   */
  async updateItemPositions(items: { id: string; position: number }[]): Promise<void> {
    const updates = items.map(item =>
      supabase.from('order_items2').update({ position: item.position }).eq('id', item.id)
    );
    await Promise.all(updates);
  }

  /**
   * Mettre à jour une ligne de commande dans order_items2
   */
  async updateOrderItem(id: string, itemData: UpdateOrderItemData): Promise<OrderItem | null> {
    try {
      const { data, error } = await supabase
        .from('order_items2')
        .update(itemData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Erreur lors de la mise à jour de la ligne de commande:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Erreur dans updateOrderItem2:', error);
      throw error;
    }
  }

  /**
   * Supprimer une ligne de commande dans order_items2
   */
  async deleteOrderItem(id: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.from('order_items2').delete().eq('id', id).select();

      if (error) {
        console.error('❌ Erreur lors de la suppression de la ligne de commande:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        throw new Error(
          `Aucune ligne supprimée - l'article avec l'ID ${id} n'existe peut-être pas`
        );
      }

      return true;
    } catch (error) {
      console.error('❌ Erreur dans deleteOrderItem2:', error);
      throw error;
    }
  }

  /**
   * Créer une nouvelle commande dans orders2
   */
  async createOrder(orderData: CreateOrderData): Promise<Order | null> {
    try {
      const dataToInsert = {
        ...orderData,
        order_type: orderData.order_type || 'BONS DE COMMANDE',
        order_date: orderData.order_date || new Date().toISOString().split('T')[0],
        status: orderData.status || 'brouillon',
      };

      const { data, error } = await supabase
        .from('orders2')
        .insert([dataToInsert])
        .select()
        .single();

      if (error) {
        console.error('Erreur lors de la création de la commande (orders2):', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Erreur dans createOrder (orders2):', error);
      throw error;
    }
  }

  /**
   * Mettre à jour une commande dans orders2
   */
  async updateOrder(id: string, orderData: UpdateOrderData): Promise<Order | null> {
    try {
      const { data, error } = await supabase
        .from('orders2')
        .update(orderData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Erreur lors de la mise à jour de la commande:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Erreur dans updateOrder2:', error);
      throw error;
    }
  }

  /**
   * Récupère les commandes paginées avec recherche et filtres
   */
  async getOrdersPaginated(
    page: number,
    limit: number,
    searchTerm: string = '',
    storeFilter?: string,
    dateFrom?: string,
    dateTo?: string,
    sortDirection: 'asc' | 'desc' = 'desc'
  ): Promise<{ orders: OrderWithClient[]; totalCount: number }> {
    const from = (page - 1) * limit;
    const to = from + limit - 1;

    // Requête de base
    let query = supabase.from('orders2').select('*', { count: 'exact' });

    // Filtre par point de vente
    if (storeFilter) {
      query = query.eq('store_id', storeFilter);
    }

    // Filtre par date (ISO YYYY-MM-DD inclusif)
    if (dateFrom) {
      query = query.gte('order_date', dateFrom);
    }
    if (dateTo) {
      query = query.lte('order_date', dateTo);
    }

    // Recherche côté serveur simplifiée (uniquement order_number)
    // La recherche étendue se fera côté client si nécessaire
    const shouldFilterClientSide = searchTerm && searchTerm.trim().length > 0;

    let data: any[];
    let count: number | null;
    let error: any;

    const ascending = sortDirection === 'asc';

    if (shouldFilterClientSide) {
      // Récupérer plus de données pour filtrer côté client
      const result = await query.order('created_at', { ascending }).limit(1000);
      data = result.data || [];
      error = result.error;
      count = null;
    } else {
      // Pagination normale
      const result = await query.order('created_at', { ascending }).range(from, to);
      data = result.data || [];
      error = result.error;
      count = result.count;
    }

    if (error) {
      console.error('❌ Erreur lors de la récupération des commandes (orders2):', error);
      throw error;
    }

    // Récupérer les IDs clients et magasins uniques
    const clientIds = [
      ...new Set((data || []).map((order: any) => order.client_id).filter(Boolean)),
    ];
    const storeIds = [...new Set((data || []).map((order: any) => order.store_id).filter(Boolean))];

    // Récupérer les clients
    const clientsMap = new Map<string, any>();
    if (clientIds.length > 0) {
      const { data: clientsData } = await supabase
        .from('clients')
        .select('id, full_name, postal_code, city, phone')
        .in('id', clientIds);

      if (clientsData) {
        clientsData.forEach(client => clientsMap.set(client.id, client));
      } else {
      }
    }

    // Récupérer les magasins
    const storesMap = new Map<string, any>();
    if (storeIds.length > 0) {
      const { data: storesData } = await supabase
        .from('stores')
        .select('id, name, initiales')
        .in('id', storeIds);

      if (storesData) {
        storesData.forEach(store => storesMap.set(store.id, store));
      } else {
      }
    }

    // Récupérer les acomptes depuis payments2
    const orderIds = (data || []).map((order: any) => order.id).filter(Boolean);
    const paymentsMap = new Map<string, number>();
    if (orderIds.length > 0) {
      const { data: paymentsData } = await supabase
        .from('payments2')
        .select('order_id, amount')
        .in('order_id', orderIds);

      if (paymentsData) {
        // Calculer le total des acomptes par commande
        paymentsData.forEach(payment => {
          const currentTotal = paymentsMap.get(payment.order_id) || 0;
          paymentsMap.set(payment.order_id, currentTotal + (payment.amount || 0));
        });
      } else {
      }
    }

    // Récupérer les commandes facturées
    const { data: invoicesData } = await supabase
      .from('invoices')
      .select('order_id')
      .not('order_id', 'is', null);
    const invoicedOrderIds = new Set(
      (invoicesData || []).map((inv: any) => inv.order_id).filter(Boolean)
    );

    // Mapper les résultats
    let orders: OrderWithClient[] = (data || []).map((order: any) => {
      const client = order.client_id ? clientsMap.get(order.client_id) : null;
      const store = order.store_id ? storesMap.get(order.store_id) : null;
      const totalAcomptes = paymentsMap.get(order.id) || 0;

      return {
        id: order.id,
        order_number: order.order_number || 'Sans numéro',
        legacy_order_number: order.legacy_order_number,
        order_date: order.order_date || new Date().toISOString().split('T')[0],
        status: order.status || 'brouillon',
        total_ttc: order.total_ttc || 0,
        subtotal_ht: order.total_htva || 0,
        total_tax: order.total_tva || 0,
        total_acomptes: totalAcomptes,
        paid_amount: totalAcomptes,
        remaining_amount: (order.total_ttc || 0) - totalAcomptes,
        order_type: order.order_type || 'BONS DE COMMANDE',
        client_name: client?.full_name || 'Client inconnu',
        client_postal_code: client?.postal_code || '-',
        client_city: client?.city || '-',
        client_phone: client?.phone || '-',
        store_id: order.store_id,
        store_name: store?.name,
        store_initiales: store?.initiales,
        items_count: 0,
        created_at: order.created_at,
        is_invoiced: invoicedOrderIds.has(order.id),
        is_closed: !!order.is_closed,
      } as OrderWithClient;
    });

    // Filtrage côté client si nécessaire
    if (shouldFilterClientSide) {
      const term = searchTerm.trim().toLowerCase();
      orders = orders.filter(
        order =>
          order.order_number?.toLowerCase().includes(term) ||
          String(order.legacy_order_number ?? '')
            .toLowerCase()
            .includes(term) ||
          order.client_name?.toLowerCase().includes(term) ||
          false ||
          order.client_phone?.toLowerCase().includes(term) ||
          false ||
          order.client_postal_code?.toLowerCase().includes(term) ||
          false ||
          order.client_city?.toLowerCase().includes(term) ||
          false ||
          order.total_ttc?.toString().includes(term) ||
          false
      );

      count = orders.length;
      orders = orders.slice(from, to + 1);
    }

    return { orders, totalCount: count || 0 };
  }

  /**
   * Mettre à jour les totaux d'une commande
   */
  async updateOrderTotals(orderId: string): Promise<void> {
    try {
      const { error } = await supabase.rpc('recalculate_order2_totals', {
        p_order_id: orderId,
      });

      if (error) {
        console.error('Erreur lors du recalcul des totaux (orders2):', error);
        throw error;
      }
    } catch (error) {
      console.error('Erreur dans updateOrderTotals2:', error);
      throw error;
    }
  }

  /**
   * Cloturer une commande (verrouille les modifications)
   */
  async closeOrder(orderId: string): Promise<boolean> {
    const { error } = await supabase
      .from('orders2')
      .update({ is_closed: true, closed_at: new Date().toISOString() })
      .eq('id', orderId);
    if (error) {
      console.error('Erreur lors de la clôture de la commande (orders2):', error);
      throw error;
    }
    return true;
  }

  /**
   * Réouvrir une commande clôturée
   */
  async reopenOrder(orderId: string): Promise<boolean> {
    const { error } = await supabase
      .from('orders2')
      .update({ is_closed: false, closed_at: null })
      .eq('id', orderId);
    if (error) {
      console.error('Erreur lors de la réouverture de la commande (orders2):', error);
      throw error;
    }
    return true;
  }

  /**
   * Supprimer une commande et toutes ses données liées (orders2)
   */
  async deleteOrder(id: string): Promise<boolean> {
    try {
      const { error } = await supabase.rpc('delete_order2', {
        p_order_id: id,
      });

      if (error) {
        console.error('❌ Erreur lors de la suppression de la commande (orders2):', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('❌ Erreur dans deleteOrder (orders2):', error);
      throw error;
    }
  }

  /**
   * Analyse toutes les commandes ouvertes d'une période (tous magasins) et les classe selon
   * les mêmes règles que "Conseils de gestion" (OrderSidePanel2) :
   * - readyToCloseIds : solde à 0 et paiements 100% banque → équivalent "Prêt à cloturer"
   * - emptyToResetIds : aucun article ni acompte (solde = total_ttc = 0) → équivalent "Document vide"
   * Utilisé par la clôture automatique de période (OrderFormPage2).
   */
  async getOrdersReadyForAutoProcessing(
    dateFrom: string,
    dateTo: string
  ): Promise<{ readyToCloseIds: string[]; emptyToResetIds: string[] }> {
    const { data: orders, error } = await supabase
      .from('orders2')
      .select('id, total_ttc')
      .eq('is_closed', false)
      .gte('order_date', dateFrom)
      .lte('order_date', dateTo);

    if (error) {
      console.error('Erreur récupération commandes pour traitement automatique:', error);
      throw error;
    }

    const orderIds = (orders || []).map(o => o.id);
    if (orderIds.length === 0) {
      return { readyToCloseIds: [], emptyToResetIds: [] };
    }

    const paymentsByOrder = new Map<string, { amount: number; payment_method: string | null }[]>();
    const CHUNK_SIZE = 100;
    for (let i = 0; i < orderIds.length; i += CHUNK_SIZE) {
      const chunk = orderIds.slice(i, i + CHUNK_SIZE);
      const { data: paymentsChunk, error: paymentsError } = await supabase
        .from('payments2')
        .select('order_id, amount, payment_method')
        .in('order_id', chunk);
      if (paymentsError) {
        console.error('Erreur récupération paiements pour traitement automatique:', paymentsError);
        throw paymentsError;
      }
      (paymentsChunk || []).forEach(p => {
        const list = paymentsByOrder.get(p.order_id) || [];
        list.push({ amount: p.amount || 0, payment_method: p.payment_method });
        paymentsByOrder.set(p.order_id, list);
      });
    }

    const readyToCloseIds: string[] = [];
    const emptyToResetIds: string[] = [];

    for (const order of orders || []) {
      const payments = paymentsByOrder.get(order.id) || [];
      const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
      const solde = (order.total_ttc || 0) - totalPaid;

      const keys = computeOrderAdviceKeys({
        solde,
        payments,
        isOrderClosed: false,
      });

      if (keys.includes('ready_to_close')) readyToCloseIds.push(order.id);
      if (keys.includes('empty_document')) emptyToResetIds.push(order.id);
    }

    return { readyToCloseIds, emptyToResetIds };
  }

  /**
   * Clôture en masse (chunké) — même effet que closeOrder() mais pour plusieurs commandes.
   */
  async closeOrdersBulk(orderIds: string[]): Promise<void> {
    const CHUNK_SIZE = 100;
    for (let i = 0; i < orderIds.length; i += CHUNK_SIZE) {
      const chunk = orderIds.slice(i, i + CHUNK_SIZE);
      const { error } = await supabase
        .from('orders2')
        .update({ is_closed: true, closed_at: new Date().toISOString() })
        .in('id', chunk);
      if (error) {
        console.error('Erreur lors de la clôture en masse (orders2):', error);
        throw error;
      }
    }
  }

  /**
   * Réinitialise les données d'une commande (articles, acomptes espèces, client/vendeur/livraison,
   * totaux) sans toucher aux paiements banque. Logique extraite de handleResetOrder
   * (OrderFormPage2) pour être réutilisable par la clôture automatique de période.
   * Retourne le total des paiements restants (banque, non supprimés).
   */
  async resetOrderData(orderId: string, defaultClientId: string): Promise<number> {
    const { error: itemsError } = await supabase
      .from('order_items2')
      .delete()
      .eq('order_id', orderId);
    if (itemsError) console.error('Erreur suppression items2:', itemsError);

    const allowedPaymentMethods = ['Espèces', "Bon d'achat", 'Espèce à la livraison'];
    const { error: paymentsError } = await supabase
      .from('payments2')
      .delete()
      .eq('order_id', orderId)
      .in('payment_method', allowedPaymentMethods);
    if (paymentsError) console.error('Erreur suppression payments2:', paymentsError);

    const { data: remainingPayments } = await supabase
      .from('payments2')
      .select('amount')
      .eq('order_id', orderId);
    const remainingTotal = remainingPayments?.reduce((sum, p) => sum + (p.amount || 0), 0) || 0;

    const { error: updateError } = await supabase
      .from('orders2')
      .update({
        client_id: defaultClientId,
        total_htva: 0,
        total_tva: 0,
        total_ttc: 0,
        vendor_id: null,
        delivery_date: null,
        delivery_info: null,
      })
      .eq('id', orderId);
    if (updateError) console.error('Erreur update orders2:', updateError);

    return remainingTotal;
  }
}

export const orderService2 = new OrderService2();
